import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PageHeader } from '../components/Navigation';
import { getBackPath } from '../utils/navigation';
import '../InvoiceDetail.css';
import '../Clients.css';
import { apiFetch } from '../utils/api';
import { useClientDetail } from '../hooks/useClients';

export default function EndUserCreatePage() {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, status, error } = useClientDetail(clientId);
  const client = data?.client;
  const backPath = getBackPath(location, clientId ? `/clients/${clientId}` : '/clients');

  const breadcrumbs = [
    { label: 'Validare', to: '/validation-hub' },
    { label: 'Emitenți', to: '/clients' },
    { label: client?.nume || 'Emitent', to: clientId ? `/clients/${clientId}` : '/clients' },
    { label: 'Creare Beneficiar', to: null }
  ];

  // --- MODIFICATION 1: Use a generic 'identifier' in the form state ---
  const [form, setForm] = useState({
    nume: '',
    identifier: '', // Replaces 'cui'
    nr_reg_com: '',
    adresa: '',
    oras: '',
    judet: '',
    tara_cod: 'RO',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [autoFilling, setAutoFilling] = useState(false);

  // --- MODIFICATION 2: Make validation logic smarter ---
  const errors = useMemo(() => {
    const e = {};
    const isCNP = form.identifier.trim().length === 13 && /^\d+$/.test(form.identifier.trim());

    if (!form.nume.trim()) e.nume = 'Denumirea este obligatorie';
    if (!form.identifier.trim()) e.identifier = 'CUI / CNP este obligatoriu';
    
    // Nr. Reg. Com. is only required if the identifier is NOT a CNP
    if (!isCNP && !form.nr_reg_com.trim()) {
      e.nr_reg_com = 'Nr. Reg. Com. este obligatoriu pentru firme';
    }

    if (!form.adresa.trim()) e.adresa = 'Adresa este obligatorie';
    if (!form.oras.trim()) e.oras = 'Orașul este obligatoriu';
    if (!form.judet.trim()) e.judet = 'Județul este obligatoriu';
    if (!form.tara_cod.trim()) e.tara_cod = 'Țara este obligatorie';
    return e;
  }, [form]);
  // --- END OF MODIFICATION ---

  const allValid = Object.keys(errors).length === 0;

  async function handleAutoFill() {
    const cui = form.identifier.trim();
    if (!cui) {
      setMessage({ type: 'error', text: 'Introduceți CUI-ul pentru a completa automat' });
      return;
    }

    // Check if it looks like a CNP
    const cuiClean = cui.toUpperCase().replace('RO', '').trim();
    if (cuiClean.length === 13 && /^\d+$/.test(cuiClean)) {
      setMessage({ type: 'error', text: 'Completarea automată funcționează doar pentru CUI (firme), nu pentru CNP' });
      return;
    }

    setAutoFilling(true);
    setMessage(null);

    try {
      const res = await apiFetch(`/api/anaf/company-lookup?cui=${encodeURIComponent(cui)}`);
      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Eroare la căutarea în ANAF');
      }

      const data = json.data;
      // Populate form fields with ANAF data (only non-empty values)
      setForm((f) => ({
        ...f,
        nume: data.nume || f.nume,
        nr_reg_com: data.nr_reg_com || f.nr_reg_com,
        adresa: data.adresa || f.adresa,
        oras: data.oras || f.oras,
        judet: data.judet || f.judet,
        tara_cod: data.tara_cod || f.tara_cod,
      }));

      const vatStatus = data.scp_tva ? 'Plătitor TVA' : 'Neplatitor TVA';
      setMessage({ type: 'success', text: `Date completate cu succes (${vatStatus})` });

    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setAutoFilling(false);
    }
  }

  async function handleSave() {
    if (!allValid) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await apiFetch(`/api/clients/${clientId}/end-users`, {
        method: 'POST',
        body: JSON.stringify(form), // The backend expects the 'identifier' field
      });
      if (!res.ok) {
        let msg = 'Eroare la crearea beneficiarului';
        try { const p = await res.clone().json(); if (p?.msg) msg = p.msg; } catch (_) {}
        throw new Error(msg);
      }
      const json = await res.json();
      const id = json?.end_user?.id;
      if (id) {
        navigate(`/end-users/${id}`); // Navigate to the new detail page
      } else {
        navigate(`/clients/${clientId}`); // Fallback navigation
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="invoice-detail">
      <PageHeader
        backTo={backPath}
        backLabel="Înapoi la Emitent"
        title="Creare Beneficiar"
        subtitle="Completați datele pentru noul beneficiar"
        breadcrumbs={breadcrumbs}
      />

      <main className="invoice-detail-main">
        <div className="invoice-detail-content">
          {message && (
            <div className={`validation-message ${message.type === 'error' ? 'validation-message-error' : 'validation-message-success'}`}>
              <p className="validation-message-text">{message.text}</p>
            </div>
          )}

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Emitent</h2>
              <p className="section-subtitle">Beneficiarul va fi asociat acestui emitent</p>
            </div>
            <div className="section-content">
              {status === 'loading' && 'Se încarcă...'}
              {status === 'error' && <div style={{ color: '#b91c1c' }}>{error}</div>}
              {status === 'success' && client && (
                <div className="form-grid form-grid-2">
                  <Field label="Denumire" value={client.nume || '—'} />
                  <Field label="CUI" value={client.cui || '—'} mono />
                </div>
              )}
            </div>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Date Beneficiar</h2>
              <p className="section-subtitle">Dacă la CUI / CNP completați 13 caractere agentul va ști ca este persoană fizică.</p>
            </div>
            <div className="section-content">
              <div className="form-grid form-grid-2">
                <Input label="Denumire / Nume" value={form.nume} onChange={(v) => setForm((f) => ({ ...f, nume: v }))} error={errors.nume} />

                {/* CUI/CNP field with auto-fill button */}
                <div className="form-field-with-autofill">
                  <Input label="CUI / CNP" value={form.identifier} onChange={(v) => setForm((f) => ({ ...f, identifier: v }))} error={errors.identifier} mono />
                  <button
                    type="button"
                    onClick={handleAutoFill}
                    disabled={autoFilling || !form.identifier.trim()}
                    className={`autofill-btn ${autoFilling ? 'autofill-btn-loading' : ''}`}
                    title="Completează automat datele firmei din ANAF"
                  >
                    {autoFilling ? 'Se caută...' : 'Completează automat'}
                  </button>
                </div>

                <Input label="Nr. Reg. Com. (doar pentru CUI)" value={form.nr_reg_com} onChange={(v) => setForm((f) => ({ ...f, nr_reg_com: v }))} error={errors.nr_reg_com} mono />
                
                <Input label="Adresă" value={form.adresa} onChange={(v) => setForm((f) => ({ ...f, adresa: v }))} error={errors.adresa} full />
                <Input label="Oraș" value={form.oras} onChange={(v) => setForm((f) => ({ ...f, oras: v }))} error={errors.oras} />
                <Input label="Județ" value={form.judet} onChange={(v) => setForm((f) => ({ ...f, judet: v }))} error={errors.judet} />
                <Input label="Țara" value={form.tara_cod} onChange={(v) => setForm((f) => ({ ...f, tara_cod: v }))} error={errors.tara_cod} />
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-content">
              <div className="actions-container">
                <div className="actions-info">
                  <h3>Acțiuni</h3>
                  <p>Salvarea este posibilă doar dacă toate câmpurile obligatorii sunt completate</p>
                </div>
                <div className="actions-buttons">
                  <button type="button" onClick={handleSave} disabled={!allValid || saving} className={`action-btn ${(!allValid || saving) ? 'action-btn-disabled' : 'action-btn-save'}`}>
                    {saving ? 'Se salvează...' : 'Salvează Beneficiar'}
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

function Field({ label, value, full = false, mono = false }) {
  return (
    <div className={full ? 'form-field party-address' : 'form-field'}>
      <div className="form-field-label">{label}</div>
      <div className={`form-field-value ${mono ? 'form-field-value-mono' : ''}`}>{String(value)}</div>
    </div>
  );
}

function Input({ label, value, onChange, mono = false, error, full = false }) {
  return (
    <div className={full ? 'form-field party-address' : 'form-field'}>
      <div className={`form-field-label ${error ? 'form-field-label-unknown' : ''}`}>{label}</div>
      <input className={`clients-input ${mono ? 'form-field-value-mono' : ''} ${error ? 'form-field-input-error' : ''}`} value={value} onChange={(e) => onChange(e.target.value)} />
      {error && <div className="form-field-error">{error}</div>}
    </div>
  );
}

